"use client";
import { motion } from "framer-motion";
import { useState, useEffect } from 'react';
import { PaperAirplaneIcon, CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    subject: 'Demande d\'information'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de l\'envoi');
      }

      setIsLoading(false);
      setIsSubmitted(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
        subject: 'Demande d\'information'
      });

    } catch (error) {
      setIsLoading(false);
      console.error('Erreur lors de l\'envoi:', error);
      alert('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: isMobile ? 0.3 : 0.5, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: isMobile ? 0.95 : 0.8 },
    show: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-3xl p-10 text-center shadow-xl relative overflow-hidden group"
      >
        {/* Effet de brillance */}
        {!isMobile && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
        )}
        
        <div className="relative">
          <motion.div
            animate={!isMobile ? { rotate: 360 } : {}}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg"
          >
            <CheckCircleIcon className="w-10 h-10 text-white" />
          </motion.div>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Message envoyé !</h3>
          <p className="text-gray-600 mb-8 text-lg leading-relaxed">
            Merci pour votre message. Notre équipe vous contactera dans les plus brefs délais.
          </p>
          <motion.button
            onClick={() => setIsSubmitted(false)}
            whileHover={!isMobile ? { scale: 1.05 } : {}}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center space-x-2"
          >
            <SparklesIcon className="w-5 h-5" />
            <span>Envoyer un autre message</span>
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: isMobile ? 0.05 : 0.1, margin: isMobile ? "-50px" : "-100px" }}
      className="bg-gradient-to-br from-white to-gray-50/30 rounded-3xl shadow-2xl p-10 border-2 border-gray-200 hover:border-violet-200 transition-all duration-300 relative overflow-hidden group"
    >
      {/* Effet de brillance au survol */}
      {!isMobile && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-violet-100/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
      )}
      
      <div className="relative">
        {/* Titre sans badge */}
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-violet-600 to-purple-600 gradient-text-safe">
              Envoyez-nous
            </span>
            <span className="block text-gray-900">un message</span>
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div variants={fadeInUp}>
              <label htmlFor="name" className="block text-base font-semibold text-gray-700 mb-3">
                Nom complet *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-300 bg-white/80 backdrop-blur-sm text-lg"
                placeholder="Votre nom"
              />
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <label htmlFor="email" className="block text-base font-semibold text-gray-700 mb-3">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-300 bg-white/80 backdrop-blur-sm text-lg"
                placeholder="votre@email.fr"
              />
            </motion.div>
          </div>
          
          <motion.div variants={fadeInUp}>
            <label htmlFor="company" className="block text-base font-semibold text-gray-700 mb-3">
              Entreprise
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-300 bg-white/80 backdrop-blur-sm text-lg"
              placeholder="Nom de votre entreprise"
            />
          </motion.div>
          
          <motion.div variants={fadeInUp}>
            <label htmlFor="subject" className="block text-base font-semibold text-gray-700 mb-3">
              Sujet
            </label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-300 bg-white/80 backdrop-blur-sm text-lg"
            >
              <option value="Demande d'information">Demande d'information</option>
              <option value="Démonstration">Demander une démonstration</option>
              <option value="Tarification">Questions sur la tarification</option>
              <option value="Support technique">Support technique</option>
              <option value="Autre">Autre</option>
            </select>
          </motion.div>
          
          <motion.div variants={fadeInUp}>
            <label htmlFor="message" className="block text-base font-semibold text-gray-700 mb-3">
              Message *
            </label>
            <textarea
              id="message"
              name="message"
              rows={6}
              required
              value={formData.message}
              onChange={handleChange}
              className="w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-300 resize-none bg-white/80 backdrop-blur-sm text-lg"
              placeholder="Décrivez votre projet et vos besoins..."
            />
          </motion.div>
          
          <motion.button
            variants={scaleIn}
            type="submit"
            disabled={isLoading}
            whileHover={!isMobile ? { scale: 1.02 } : {}}
            whileTap={{ scale: 0.98 }}
            className={`w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white px-10 py-5 rounded-2xl font-bold text-xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center space-x-3 relative overflow-hidden group ${
              isLoading ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            {/* Effet de brillance */}
            {!isMobile && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
            )}
            
            <div className="relative flex items-center space-x-3">
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <>
                  <motion.div
                    animate={!isMobile ? { rotate: [0, 15, -15, 0] } : {}}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <PaperAirplaneIcon className="w-6 h-6" />
                  </motion.div>
                  <span>Envoyer le message</span>
                </>
              )}
            </div>
          </motion.button>
        </form>
        
        <motion.p 
          variants={fadeInUp}
          className="text-sm text-gray-500 text-center mt-8 leading-relaxed"
        >
          * Champs obligatoires. Vos données sont protégées et ne seront jamais partagées.
        </motion.p>
      </div>
    </motion.div>
  );
} 